/**
 * Reference implementation of a canvas provider.
 *
 * This wires up a concrete feature set (pan, zoom, rotation, translations, …)
 * and composes the store. Production canvases should follow the same pattern
 * but pick their own features and configuration:
 *
 * 1. Define `createFeatures()` with the features you need.
 * 2. Compose slices via `withCameraSlice(withDocumentPagesSlice(featureSlices))`.
 * 3. Extract transform contributions and effective dimensions from features.
 * 4. Wrap children in `<CanvasProvider>` + `<DocumentCanvasContextProvider>`.
 *
 * See {@link DocumentCanvas} for the matching UI shell.
 */
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  CanvasProvider,
  composeStore,
  createStoreHooks,
  getHandlers,
  type InferCanvasStore,
  withCameraSlice,
} from '../../canvas/core';
import {
  createAutoPanFeature,
  createCursorTrackingFeature,
  createPanFeature,
  createRectangleSelectionFeature,
  createZoomFeature,
} from '../../canvas/features';
import {
  composeGetEffectiveDimensions,
  DocumentCanvasContextProvider,
  extractTransformContributions,
  getTransformWrappers,
  withDocumentPagesSlice,
} from '../core';
import { createPageCanvasOverlayFeature } from '../features/page-canvas-overlay';
import { createRotationFeature } from '../features/page-rotation';
import { createTranslationLayerFeature } from '../features/translation';
import { useInitialFit } from './useInitialFit';

const createFeatures = () =>
  [
    createAutoPanFeature(),
    createCursorTrackingFeature(),
    createRectangleSelectionFeature(),
    createPanFeature(),
    createZoomFeature(),
    createRotationFeature(),
    createTranslationLayerFeature(),
    createPageCanvasOverlayFeature(),
  ] as const;

type Features = ReturnType<typeof createFeatures>;

type FeatureSlice = NonNullable<Features[number]['slice']>;

const createDocumentCanvasStore = () => {
  const features = createFeatures();

  const featureSlices = features
    .map(f => f.slice)
    .filter((s): s is FeatureSlice => s !== undefined);

  const slices = withCameraSlice(withDocumentPagesSlice(featureSlices));

  const { store } = composeStore(...slices);

  const contributions = extractTransformContributions(features, store.getState);
  const getEffectiveDimensions = composeGetEffectiveDimensions(features);
  const transformWrappers = getTransformWrappers(contributions);

  return {
    store,
    handlers: getHandlers(features),
    getEffectiveDimensions,
    transformWrappers,
  };
};

export type DocumentCanvasStore = InferCanvasStore<
  typeof createDocumentCanvasStore
>;

const { useStore, useStoreApi } = createStoreHooks<DocumentCanvasStore>();

type DocumentCanvasProviderProps = {
  children: React.ReactNode;
};

export const DocumentCanvasProvider = ({
  children,
}: DocumentCanvasProviderProps) => {
  const { store, handlers, getEffectiveDimensions, transformWrappers } =
    useMemo(() => createDocumentCanvasStore(), []);

  useInitialFit(store);

  const contextValue = useMemo(
    () => ({ getEffectiveDimensions, transformWrappers }),
    [getEffectiveDimensions, transformWrappers]
  );

  return (
    <CanvasProvider store={store} handlers={handlers}>
      <DocumentCanvasContextProvider value={contextValue}>
        {children}
      </DocumentCanvasContextProvider>
    </CanvasProvider>
  );
};

export const useDocumentCanvasStore = useStore;
/** @public */
export const useDocumentCanvasStoreApi = useStoreApi;

export const useDocumentCanvasActions = () =>
  useDocumentCanvasStore(
    useShallow(state => ({
      ...state.canvas.actions,
      ...state.zoom.actions,
      ...state.pan.actions,
    }))
  );
